import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Search,
  BookOpen,
  GraduationCap,
  Clock,
  Award,
  Filter,
  Star,
  CheckCircle,
  ArrowRight,
  Sparkles,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { Course, CourseCategory } from '../../types/database';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function CatalogPage() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [categories, setCategories] = useState<CourseCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedLevel, setSelectedLevel] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Fetch Categories
      const { data: cats, error: catErr } = await supabase
        .from('course_categories')
        .select('*')
        .order('display_order');
      if (catErr) console.error('[CATALOG] Categories error:', catErr);
      if (cats) setCategories(cats);

      // 2. Fetch Courses
      const { data: crs, error: crsErr } = await supabase
        .from('courses')
        .select('*, category:course_categories(*)')
        .eq('status', 'Published')
        .order('display_order');

      if (crsErr) {
        console.error('[CATALOG] Courses error:', crsErr);
        setError('Unable to load courses. Please try again.');
      }
      if (crs) setCourses(crs as Course[]);
    } catch (err) {
      console.error('[CATALOG] Unexpected error:', err);
      setError('Unable to load courses. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredCourses = courses.filter((course) => {
    const matchesCategory =
      selectedCategory === 'all' || course.category?.slug === selectedCategory;
    const matchesLevel =
      selectedLevel === 'all' || course.level === selectedLevel;
    const matchesSearch =
      course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.short_description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesLevel && matchesSearch;
  });

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-darkBlue-900 via-darkBlue-800 to-red-800 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black/25"></div>
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-clairBlue-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-red-500/10 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-6 relative z-10 text-center max-w-4xl">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full text-xs font-semibold uppercase tracking-wider mb-6">
            <Sparkles className="w-4 h-4 text-clairBlue-300" />
            <span>Practical Skills for Employment & Freelancing</span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight mb-6">
            BientoMart Online Academy
          </h1>

          <p className="text-lg sm:text-xl text-blue-100 max-w-2xl mx-auto leading-relaxed mb-10">
            Learn high-demand computer skills, office productivity, and artificial intelligence from verified industry experts. Earn verifiable certificates to elevate your career.
          </p>

          {/* Search Box */}
          <div className="max-w-2xl mx-auto relative">
            <Search className="w-6 h-6 absolute left-5 top-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search courses: Excel, Word, AI Tools, Freelancing, CV..."
              className="w-full pl-14 pr-6 py-4 rounded-2xl bg-white text-gray-900 shadow-2xl focus:outline-none focus:ring-4 focus:ring-clairBlue-400/40 text-base"
            />
          </div>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="bg-white border-b border-gray-200 sticky top-16 z-30 shadow-sm">
        <div className="container mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-4">
          {/* Categories */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0 scrollbar-none">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                selectedCategory === 'all'
                  ? 'bg-darkBlue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              All Categories
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.slug)}
                className={`px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                  selectedCategory === cat.slug
                    ? 'bg-darkBlue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* Level Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e.target.value)}
              className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-xl text-xs font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-clairBlue-500"
            >
              <option value="all">All Difficulty Levels</option>
              <option value="Beginner">Beginner</option>
              <option value="Intermediate">Intermediate</option>
              <option value="Advanced">Advanced</option>
              <option value="All Levels">All Levels</option>
            </select>
          </div>
        </div>
      </section>

      {/* Course Catalog Grid */}
      <main className="flex-1 py-12">
        <div className="container mx-auto px-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-12 h-12 border-4 border-darkBlue-600 border-t-transparent rounded-full animate-spin mb-4" />
              <p className="text-gray-500 text-sm font-medium">Loading academy courses...</p>
            </div>
          ) : error ? (
            <div className="text-center py-20 bg-white rounded-3xl border border-red-100 p-12">
              <BookOpen className="w-16 h-16 text-red-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-700 mb-2">Unable to load courses</h3>
              <p className="text-gray-500 text-sm max-w-md mx-auto mb-6">{error}</p>
              <button
                onClick={fetchData}
                className="px-5 py-2.5 bg-darkBlue-600 text-white rounded-xl text-sm font-semibold hover:bg-darkBlue-700"
              >
                Try Again
              </button>
            </div>
          ) : filteredCourses.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-3xl border border-gray-100 p-12">
              <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-700 mb-2">No courses found</h3>
              <p className="text-gray-500 text-sm max-w-md mx-auto mb-6">
                We couldn't find any courses matching "{searchQuery}". Try searching for another topic or resetting filters.
              </p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('all');
                  setSelectedLevel('all');
                }}
                className="px-5 py-2.5 bg-darkBlue-600 text-white rounded-xl text-sm font-semibold hover:bg-darkBlue-700"
              >
                Reset Filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredCourses.map((course, idx) => (
                <motion.div
                  key={course.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="bg-white rounded-3xl shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 flex flex-col overflow-hidden group"
                >
                  {/* Card Header Banner */}
                  <div className="h-40 bg-gradient-to-br from-darkBlue-900 via-darkBlue-800 to-clairBlue-900 relative p-5 flex flex-col justify-between text-white overflow-hidden">
                    <div className="absolute -right-6 -bottom-6 w-28 h-28 bg-white/10 rounded-full blur-xl group-hover:scale-125 transition-transform" />
                    <div className="flex items-center justify-between relative z-10">
                      <span className="px-3 py-1 bg-white/20 backdrop-blur-md rounded-full text-xs font-semibold">
                        {course.category?.name || 'General'}
                      </span>
                      <span className="text-xs bg-amber-400/20 text-amber-300 border border-amber-400/30 px-2.5 py-0.5 rounded-full font-bold">
                        {course.level}
                      </span>
                    </div>
                    <div className="relative z-10 flex items-center gap-2">
                      <GraduationCap className="w-6 h-6 text-clairBlue-300" />
                      <span className="text-xs font-semibold text-blue-100">Verifiable Certificate</span>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="p-6 flex-1 flex flex-col justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2 leading-snug group-hover:text-clairBlue-600 transition-colors">
                        {course.title}
                      </h3>
                      <p className="text-gray-500 text-xs line-clamp-3 mb-4 leading-relaxed">
                        {course.short_description}
                      </p>
                    </div>

                    <div>
                      {/* Meta badges */}
                      <div className="flex items-center gap-4 text-xs text-gray-500 border-t border-gray-100 pt-4 mb-4">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5 text-gray-400" />
                          {course.duration_hours} hrs
                        </span>
                        <span className="flex items-center gap-1">
                          <Award className="w-3.5 h-3.5 text-emerald-500" />
                          Certificate
                        </span>
                        <span className="flex items-center gap-1 ml-auto text-amber-500 font-semibold">
                          <Star className="w-3.5 h-3.5 fill-current" />
                          4.9
                        </span>
                      </div>

                      {/* Price & CTA */}
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-xs text-gray-400 block">Tuition fee</span>
                          <span className="text-xl font-black text-gray-900">
                            {course.price === 0 ? 'Free' : `$${course.price.toFixed(2)} USD`}
                          </span>
                        </div>
                        <Link
                          to={`/academy/courses/${course.slug}`}
                          className="px-4 py-2 bg-gradient-to-r from-darkBlue-600 to-clairBlue-500 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-md hover:shadow-lg hover:scale-105 transition-all"
                        >
                          <span>View Course</span>
                          <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
